/* Per-restaurant cart (different from the single-tenant restaurant
 * CartContext). Keyed by restaurant_id so each demo site has its own basket.
 * localStorage-backed. */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

const Ctx = createContext(null);
const KEY = "hp_demo_cart_v1";

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
};
const persist = (obj) => {
  try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch (_e) { void _e; }
};

export function DemoCartProvider({ children }) {
  const [carts, setCarts] = useState(load);
  useEffect(() => persist(carts), [carts]);

  const addToCart = useCallback((rid, item) => {
    setCarts((prev) => {
      const cart = prev[rid] || { items: [], ts: Date.now() };
      const i = cart.items.findIndex((x) => String(x.id) === String(item.id));
      const items = i >= 0
        ? cart.items.map((x, idx) => idx === i ? { ...x, qty: x.qty + 1 } : x)
        : [...cart.items, {
            id: item.id, name: item.name,
            price: parseFloat(item.price) || 0,
            qty: 1, image: item.image || null,
          }];
      return { ...prev, [rid]: { items, ts: Date.now() } };
    });
  }, []);

  const updateQty = useCallback((rid, itemId, delta) => {
    setCarts((prev) => {
      const cart = prev[rid];
      if (!cart) return prev;
      const items = cart.items
        .map((x) => String(x.id) === String(itemId) ? { ...x, qty: x.qty + delta } : x)
        .filter((x) => x.qty > 0);
      return { ...prev, [rid]: { items, ts: Date.now() } };
    });
  }, []);

  const removeItem = useCallback((rid, itemId) => {
    setCarts((prev) => {
      const cart = prev[rid];
      if (!cart) return prev;
      const items = cart.items.filter((x) => String(x.id) !== String(itemId));
      return { ...prev, [rid]: { items, ts: Date.now() } };
    });
  }, []);

  const clearCart = useCallback((rid) => {
    setCarts((prev) => {
      const next = { ...prev };
      delete next[rid];
      return next;
    });
  }, []);

  const getCart = useCallback((rid) => carts[rid] || { items: [], ts: 0 }, [carts]);

  const value = useMemo(
    () => ({ getCart, addToCart, updateQty, removeItem, clearCart }),
    [getCart, addToCart, updateQty, removeItem, clearCart],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDemoCart(rid) {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDemoCart must be inside DemoCartProvider");
  const cart = c.getCart(rid);
  const subtotal = cart.items.reduce((s, i) => s + i.price * i.qty, 0);
  const itemCount = cart.items.reduce((s, i) => s + i.qty, 0);
  return {
    items: cart.items, subtotal, itemCount,
    add: (it) => c.addToCart(rid, it),
    update: (id, d) => c.updateQty(rid, id, d),
    remove: (id) => c.removeItem(rid, id),
    clear: () => c.clearCart(rid),
  };
}
